import React from 'react';
import Button from '@prisjakt/ui-button';
import ArrowIcon from 'mdi-react/ArrowTopLeftIcon';
import Chevron from 'mdi-react/ChevronRightIcon';
import PackageIcon from 'mdi-react/PackageIcon';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import qs from 'query-string-for-all';
import PriceLabel from '@prisjakt/ui-price-label';
import { SearchResult, SearchResultError } from '@prisjakt/search-types';
import actions from '../../search/actions/search';
import AdContainer from '../../../components/ads';
import { SuggestionsResponse, getSuggestionsFromOldFormat } from './oldFormat';
import { getSuggestionsFromNewFormat } from './newFormat';
import { Suggestion } from './types';

import * as S from './styles';

const adsViewUpdateAction = () => ({
  type: 'ADS_VIEW_UPDATE',
  view: `search_suggestions`,
});

type Props = {
  onClose: () => void;
  adsViewUpdateAction: () => void;
  suggestionClick: (type: Suggestion['type'], position: number) => void;
  showProductCollectionSuggestions: boolean;
  includePriceOnProductSuggestions: boolean;
  useNewResponseFormat: boolean;
  query: string;
  setQuery: (query: string) => void;
} & RouteComponentProps;

type State = {
  selected: number;
  suggestions: Suggestion[];
};

class Suggestions extends React.PureComponent<Props, State> {
  state: State = {
    selected: 0,
    suggestions: [],
  };

  input?: HTMLInputElement;

  searchbar?: Element;

  container?: Element;

  handleOutSideClick: EventListener = e => {
    if (this.input && this.input === e.target) {
      return;
    }

    if (e.target instanceof Node && this.searchbar?.contains(e.target)) {
      return;
    }

    if (e.target instanceof Node && this.container?.contains(e.target)) {
      return;
    }

    this.props.onClose();
  };

  updateAds = throttle(() => {
    this.props.adsViewUpdateAction();
  }, 1500);

  componentDidUpdate(prevProps: Props) {
    if (prevProps.query !== this.props.query) {
      this.fetchSuggestions();
      this.updateAds();
    }
  }

  componentDidMount() {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLInputElement)) {
      return;
    }

    this.updateAds();

    /**
      Find references to search bar and input field
      in order to track key changes
    * */
    this.input = Array.from(
      document.querySelectorAll('[data-suggestions="search"]')
    ).find(
      (el): el is HTMLInputElement =>
        el instanceof HTMLInputElement && el.offsetParent !== null
    );

    this.searchbar =
      document.querySelector('[data-searchbar="true"]') ?? undefined;

    this.enableKeyboardNavigation(activeElement);
    this.fetchSuggestions();
    window.addEventListener('click', this.handleOutSideClick);
  }

  componentWillUnmount() {
    if (this.input) {
      this.disableKeyboardNavigation(this.input);
    }
    window.removeEventListener('click', this.handleOutSideClick);
  }

  fetchSuggestions = debounce(
    async () => {
      const {
        query,
        showProductCollectionSuggestions,
        includePriceOnProductSuggestions,
        useNewResponseFormat,
      } = this.props;

      const { superSearchEndpoint } = this.context.market;

      if (!superSearchEndpoint) {
        return;
      }

      // HACK: Super search endpoint has rfe hardcoded at the moment
      // Fix this when refactoring full search area
      const endpoint =
        superSearchEndpoint.replace('/rfe', '') +
        (useNewResponseFormat ? '/v2' : '');

      let res;

      try {
        res = await fetch(
          `${endpoint}/suggestions?q=${query}&market=${
            this.context.market.code
          }&searchInProperties=true&offerCollections=1'${
            includePriceOnProductSuggestions ? '&includePrice=1' : ''
          }${showProductCollectionSuggestions ? '&productCollections=1' : ''}`
        );
        if (res.status !== 200) {
          const searchError: SearchResultError = await res.json();
          throw new Error(`Search failed: ${searchError.error}`);
        }
      } catch (err) {
        return;
      }

      if (query !== this.props.query) {
        return;
      }

      if (useNewResponseFormat) {
        const data: SearchResult = await res.json();

        this.setState({ suggestions: getSuggestionsFromNewFormat(data) });
      } else {
        const data: SuggestionsResponse = await res.json();

        if (data.error) {
          return;
        }

        this.setState({ suggestions: getSuggestionsFromOldFormat(data) });
      }
    },
    100,
    { leading: true, trailing: true }
  );

  handleKeyPress = (e: KeyboardEvent) => {
    const { suggestions = [], selected } = this.state;

    const selectable = [{ navId: 0, text: this.props.query }, ...suggestions];

    switch (e.keyCode) {
      // Enter
      case 13: {
        const suggestion = selectable.find(s => s.navId === selected);

        if (suggestion) {
          this.handleClick(suggestion);
          if (this.input) this.input.blur();
          e.stopPropagation();
        } else {
          this.handleClick({
            text: this.props.query,
            type: 'enter',
            navId: 0,
          });
          if (this.input) this.input.blur();
          e.stopPropagation();
        }
        break;
      }
      // Right
      case 39: {
        const suggestion = suggestions.find(s => s.navId === selected);

        if (
          suggestion &&
          this.input &&
          this.input.selectionStart === this.input.selectionEnd &&
          this.input.selectionStart === this.input.value.length
        ) {
          this.input.value = `${suggestion.text} `;
          this.input.dispatchEvent(new CustomEvent('focus'));
        }
        // Make sure search bar updates text
        break;
      }
      // Up/Down
      case 38:
      case 40: {
        e.preventDefault();
        let nextItem;
        const currentIndex = selectable.findIndex(s => s.navId === selected);
        if (e.keyCode === 40) {
          // ArrowDown
          nextItem = selectable[(currentIndex + 1) % selectable.length];
        } else {
          // ArrowUp
          const nextIndex =
            currentIndex - 1 === -1 ? selectable.length - 1 : currentIndex - 1;

          nextItem = selectable[nextIndex];
        }

        if (nextItem) {
          if (this.input) this.input.value = `${nextItem.text} `;

          this.setState({ selected: nextItem.navId });
        }
        break;
      }
      default:
        break;
    }
  };

  disableKeyboardNavigation(input: HTMLElement) {
    input.removeEventListener('keydown', this.handleKeyPress);
  }

  enableKeyboardNavigation(input: HTMLElement) {
    input.addEventListener('keydown', this.handleKeyPress);
  }

  setSuggestion(suggestion: Suggestion) {
    const query = `${suggestion.text.trim()} `;
    this.props.setQuery(query);

    if (!this.input) return;
    this.input.value = query;
    this.input.dispatchEvent(new CustomEvent('change'));
  }

  static async gotoUrl(
    type: Suggestion['type'],
    id: Suggestion['id'],
    query: string,
    meta?: string
  ) {
    const params = { q: query, meta };
    const res = await fetch(
      `${window.INTERNAL_HOSTNAME}/_internal/goto/${type}/${id}?${qs.stringify(
        params
      )}`
    );

    if (res.status !== 200) {
      throw new Error(`Cannot resolve url for ${type}`);
    }

    return res.text();
  }

  async handleClick(suggestion: Suggestion) {
    const { push } = this.props.history;

    // Log a suggestion click if the user clicked on a suggestion (not on the query itself)
    if (suggestion.navId > 0) {
      this.props.suggestionClick(suggestion.type, suggestion.navId);
    }
    this.setSuggestion(suggestion);

    switch (suggestion.type) {
      // Try to resolve correct. If fail, fall through to default
      case 'product':
      case 'vcategory':
      case 'category':
      case 'brand_category':
      case 'product_collection':
      case 'offer_collection':
      case 'store': {
        try {
          const url = await Suggestions.gotoUrl(
            suggestion.type,
            suggestion.id,
            suggestion.text,
            suggestion.meta
          );
          this.props.onClose();
          push(url);
          break;
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
      // eslint-disable-next-line no-fallthrough
      default: {
        this.props.onClose();
        push(`/search?search=${encodeURIComponent(suggestion.text)}`);
      }
    }
  }

  getIndices(s: string) {
    const c = this.props.query.toLowerCase();
    return s
      .split('')
      .reduce<number[]>((a, e, i) => (e === c ? a.concat(i) : a), []);
  }

  render() {
    const { t } = this.context;
    const { suggestions, selected } = this.state;
    const { includePriceOnProductSuggestions } = this.props;

    return (
      <>
        <S.Wrapper>
          <S.Inner>
            <S.Suggestions
              ref={node => {
                this.container = node ?? undefined;
              }}
            >
              <S.Item
                disabled={this.props.query.length < 2}
                selected={selected === 0}
                onClick={() => {
                  if (this.props.query.length < 2) {
                    return;
                  }
                  this.handleClick({
                    text: this.props.query,
                    type: 'original',
                    navId: 0,
                  });
                }}
              >
                <S.OriginalQuery
                  selected={selected === 0}
                  data-test="OriginalQuery"
                >
                  <span>
                    <T domain="search">suggest.title-original</T>{' '}
                    <i>{this.props.query}</i>
                  </span>
                  <Button
                    disabled={this.props.query.length < 2}
                    color="secondary"
                    variant="icon"
                    icon={<Chevron />}
                  />
                </S.OriginalQuery>
              </S.Item>
              {suggestions && suggestions.length >= 7 && (
                <S.AdItem style={{ order: 7 }}>
                  <AdContainer
                    key="dubbelpanorama1"
                    className="with-position-1"
                    placement="dubbelpanorama1"
                    format="panorama_1"
                    min={0}
                    max={860}
                    async
                  />
                </S.AdItem>
              )}
              {suggestions &&
                suggestions.map((suggestion, i) => (
                  <S.Item
                    key={suggestion.navId}
                    onClick={() => {
                      this.handleClick(suggestion);
                    }}
                    selected={suggestion.navId === selected}
                    style={{ order: i }}
                    data-test="SearchSuggestionItem"
                  >
                    <S.RowInfo>
                      <S.IconWrapper>
                        {suggestion.type === 'suggestion' && <S.SearchIcon />}
                        {suggestion.type === 'product' && (
                          <S.Icon
                            src={`https://cdn.pji.nu/product/standard/80/${suggestion.id}.jpg`}
                          />
                        )}
                        {suggestion.logo && suggestion.type === 'store' && (
                          <S.Icon storeLogo src={suggestion.logo} />
                        )}
                        {suggestion.logo &&
                          suggestion.type?.indexOf('category') !== -1 && (
                            <S.Icon
                              src={`https://cdn.pji.nu/g/cat/192/${suggestion.logo}.png`}
                            />
                          )}
                        {suggestion.logo &&
                          suggestion.type === 'product_collection' && (
                            <S.Icon src={suggestion.logo} />
                          )}
                        {suggestion.type === 'offer_collection' && (
                          <PackageIcon />
                        )}
                      </S.IconWrapper>
                      {suggestion.text}
                    </S.RowInfo>
                    {suggestion.type !== 'suggestion' &&
                      (!includePriceOnProductSuggestions ||
                        suggestion.type !== 'product') && (
                        <S.DirectLink data-test="DirectLink">
                          {t('search', `suggest.${suggestion.type}`)}
                        </S.DirectLink>
                      )}
                    {includePriceOnProductSuggestions &&
                      suggestion.type === 'product' && (
                        <PriceLabel value={suggestion.price || null} />
                      )}
                    {suggestion.type === 'suggestion' && (
                      <S.ExtendSearch>
                        <Button
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            this.setSuggestion(suggestion);
                            if (this.input) this.input.focus();
                          }}
                          color="secondary"
                          variant="icon"
                          icon={<ArrowIcon />}
                        />
                      </S.ExtendSearch>
                    )}
                  </S.Item>
                ))}
            </S.Suggestions>
          </S.Inner>
        </S.Wrapper>
      </>
    );
  }
}

Suggestions.contextType = AppContext;

export default connect(undefined, { ...actions, adsViewUpdateAction })(
  withRouter(Suggestions)
);
